import React, { useEffect, useState } from 'react';
import { Search, Stethoscope, Scissors, Scale, Hotel, Wrench, DraftingCompass } from 'lucide-react';
import ServiceCategoryCard from './ServiceCategoryCard';
import { getCategories } from '../supabase/queries';

interface CategoryGridProps {
  onSelectCategory: (categoryId: string) => void;
}

// --- Icon Mapping ---
// This mapping is used to assign a specific Lucide icon to a category name.
// If a category from the database doesn't have a direct mapping here,
// a default icon (e.g., Search) could be used or it could be left without an icon.
const iconMap: { [key: string]: React.ComponentType<React.SVGProps<SVGSVGElement>> } = {
  Doctor: Stethoscope,
  Salon: Scissors,
  Lawyer: Scale,
  Hotel: Hotel,
  Handyman: Wrench,
  Architect: DraftingCompass,
  // Add more mappings as needed
};

/**
 * 3. Category Grid Component
 * Contains the search bar and the grid of service category cards.
 */
const CategoryGrid: React.FC<CategoryGridProps> = ({ onSelectCategory }) => {
  const [categories, setCategories] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      setError(null);
      const data = await getCategories();
      if (data) {
        setCategories(data);
      } else {
        setError('Failed to fetch categories.');
      }
      setLoading(false);
    }
    fetchCategories();
  }, []);

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="text-white text-center py-8">Loading categories...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">Error: {error}</div>;
  }

  return (
    <div className="flex-1 overflow-y-auto px-6 pt-0 pb-24 space-y-6">
        
        {/* Search Bar */}
        <div className="relative">
            <input
                type="text"
                placeholder="Search here for category..."
                className="w-full py-4 pl-12 pr-4 bg-gray-900 border border-gray-700 text-white placeholder-gray-500 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500 shadow-inner shadow-gray-900 transition duration-150"
                aria-label="Search categories"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search className="w-5 h-5 text-gray-500 absolute left-4 top-1/2 transform -translate-y-1/2" />
        </div>

        {/* Category Grid */}
        <div className="grid grid-cols-2 gap-4">
            {filteredCategories.map((cat) => {
                const IconComponent = iconMap[cat.name] || Search; // Default to Search icon if no specific icon is mapped
                return (
                    <ServiceCategoryCard
                        key={cat.id}
                        id={cat.id} // Pass id
                        name={cat.name}
                        Icon={IconComponent}
                        onClick={onSelectCategory} // Pass onClick handler
                    />
                );
            })}
        </div>
        
        {/* Add some padding space for the floating bottom nav */}
        <div className="h-20"></div>
    </div>
  );
};

export default CategoryGrid;
